import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import { fetchPosts } from '../api';
import { combineSchemas, organizationSchema, breadcrumbSchema } from '../lib/schema';
import { Calendar, User, ArrowRight, Loader2 } from 'lucide-react';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    fetchPosts().then((data) => {
      setPosts(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const categories = ['All', ...Array.from(new Set(posts.map((p) => p.category)))];
  const visible = category === 'All' ? posts : posts.filter((p) => p.category === category);

  return (
    <Layout>
      <SEO
        title="Website Symphony Blog | Web Design, SEO & Marketing Insights"
        description="Expert insights on web design, SEO, AI, local marketing and growth for med spas, tattoo shops, contractors and energy companies. Updated weekly by Website Symphony."
        canonical="/blog"
        keywords="web design blog, SEO blog, med spa marketing, tattoo shop marketing, construction marketing, local SEO tips"
        schema={combineSchemas(organizationSchema, breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }]))}
      />
      <PageHero title="Insights & Growth Playbook" subtitle="Practical articles on web design, SEO, AI and marketing for small and mid-size businesses." breadcrumb="Blog" />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#2C5F66]" size={40} /></div>
          ) : (
            <>
              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-5 py-2 rounded-full font-medium text-sm transition-colors ${
                      category === c ? 'bg-[#2C5F66] text-white' : 'bg-[#F7F7F5] text-[#2C5F66] hover:bg-[#2C5F66]/10'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visible.map((post) => (
                  <article key={post.id} className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                    <Link to={`/blog/${post.slug}`}>
                      <div className="overflow-hidden h-52">
                        <img src={post.cover_image} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      </div>
                      <div className="p-6">
                        <span className="text-[#D9A04A] text-xs font-semibold tracking-wider uppercase">{post.category}</span>
                        <h2 className="mt-2 text-[#2C5F66] text-xl font-bold leading-tight group-hover:text-[#D9A04A] transition-colors">{post.title}</h2>
                        <p className="mt-3 text-gray-600 text-sm leading-relaxed line-clamp-3">{post.excerpt}</p>
                        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="inline-flex items-center gap-1 text-[#2C5F66] font-semibold">Read <ArrowRight size={12} /></span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
              {visible.length === 0 && <p className="text-center text-gray-500 py-16">No posts in this category yet.</p>}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
